import { useQuery } from "@tanstack/react-query"
import { farmsApi } from "@/features/farms/api/farmsApi"
import { analyticsApi } from "@/features/dashboard/api/analyticsApi"
import { queryKeys } from "@/shared/api/queryClient"
import { Card } from "@/shared/components/ui/Card"
import { formatDate } from "@/shared/lib/utils"

export function DashboardPage() {
  const { data: farms, isLoading: isFarmsLoading } = useQuery({
    queryKey: queryKeys.farms.all(),
    queryFn: farmsApi.list,
  })

  const { data: analytics, isLoading: isAnalyticsLoading } = useQuery({
    queryKey: ["analytics", "user-summary"],
    queryFn: analyticsApi.getUserSummary,
  })

  const isLoading = isFarmsLoading || isAnalyticsLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="font-roboto text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="font-roboto mt-2 text-base text-gray-600">
          Overview of your aquaculture operations
        </p>
      </div>

      {/* Stats Grid - Soft Professional Style */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Farms Card */}
        <div className="stat-card-primary group">
          <div className="stat-card-shine" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 border border-primary-200">
                <svg className="h-5 w-5 text-primary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            <p className="font-roboto text-sm font-medium text-gray-600 mb-1">Total Farms</p>
            <p className="font-roboto text-3xl font-bold text-gray-900">{farms?.length || 0}</p>
          </div>
        </div>

        {/* Active Ponds Card */}
        <div className="stat-card-secondary group">
          <div className="stat-card-shine" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 border border-secondary-200">
                <svg className="h-5 w-5 text-secondary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
            </div>
            <p className="font-roboto text-sm font-medium text-gray-600 mb-1">Active Ponds</p>
            <p className="font-roboto text-3xl font-bold text-gray-900">{analytics?.summary?.activePonds ?? 0}</p>
          </div>
        </div>

        {/* Water Tests Card */}
        <div className="stat-card-info group">
          <div className="stat-card-shine" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-100 border border-blue-200">
                <svg className="h-5 w-5 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <p className="font-roboto text-sm font-medium text-gray-600 mb-1">Water Tests</p>
            <p className="font-roboto text-3xl font-bold text-gray-900">{analytics?.summary?.totalWaterTests ?? 0}</p>
            <p className="font-roboto text-xs text-gray-500 mt-1">Last 30 days</p>
          </div>
        </div>

        {/* Health Checks Card */}
        <div className="stat-card-success group">
          <div className="stat-card-shine" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-green-100 border border-green-200">
                <svg className="h-5 w-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="font-roboto text-sm font-medium text-gray-600 mb-1">Health Checks</p>
            <p className="font-roboto text-3xl font-bold text-gray-900">{analytics?.summary?.totalHealthChecks ?? 0}</p>
            <p className="font-roboto text-xs text-gray-500 mt-1">Last 30 days</p>
          </div>
        </div>
      </div>

      {/* Recent Farms */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-roboto text-lg font-semibold text-gray-900">
            Recent Farms
          </h2>
        </div>
        {farms && farms.length > 0 ? (
          <div className="space-y-1">
            {farms.slice(0, 5).map((farm) => (
              <div
                key={farm.id}
                className="group relative flex items-center justify-between rounded-lg px-3 py-3 transition-all duration-150 hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 border border-gray-200 group-hover:border-gray-300 transition-colors">
                    <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-roboto font-medium text-gray-900">{farm.name}</p>
                    <p className="font-roboto text-sm text-gray-600">{farm.location || "No location"}</p>
                  </div>
                </div>
                <p className="font-roboto text-xs text-gray-500">
                  {formatDate(farm.createdAt)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100 border border-gray-200 mb-4">
              <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <p className="font-roboto text-sm font-medium text-gray-900 mb-1">No farms yet</p>
            <p className="font-roboto text-sm text-gray-600">Create your first farm to get started</p>
          </div>
        )}
      </Card>
    </div>
  )
}
